import type { StateCreator } from 'zustand'
import { chatBuilderService } from '@/services/chatBuilder'
import { storeLogger } from '@/utils/logger'
import type { AssetsState, ChatConfigurationAsset } from './types'

export interface AssetsSlice extends AssetsState {
  // Asset management actions
  uploadAsset: (
    configId: string,
    assetType: 'ai_messenger_icon' | 'send_button_icon',
    file: File
  ) => Promise<ChatConfigurationAsset>
  deleteAsset: (configId: string, assetTypeOrId: string) => Promise<void>

  // Internal methods (should be called by configuration slice)
  updateAssetsInCurrentConfiguration: (
    updater: (assets: ChatConfigurationAsset[]) => ChatConfigurationAsset[]
  ) => void
}

export const createAssetsSlice: StateCreator<
  AssetsSlice & {
    currentConfiguration: any
    setLoading: (loading: boolean) => void
  },
  [],
  [],
  AssetsSlice
> = (set, get) => ({
  // Initial state
  isLoading: false,
  error: null,

  // Asset management
  uploadAsset: async (
    configId: string,
    assetType: 'ai_messenger_icon' | 'send_button_icon',
    file: File
  ) => {
    set({ isLoading: true, error: null })

    try {
      const asset = await chatBuilderService.uploadAsset(
        configId,
        assetType,
        file
      )

      storeLogger.debug('Asset uploaded', {
        configId,
        assetType,
        fileName: file.name,
        fileSize: file.size,
      })

      // Update current configuration with new asset
      const { updateAssetsInCurrentConfiguration } = get()
      updateAssetsInCurrentConfiguration((existingAssets) => [
        ...existingAssets.filter((a) => a.assetType !== assetType),
        asset,
      ])

      set({ isLoading: false })
      return asset
    } catch (error) {
      storeLogger.error('Failed to upload asset', error as Error)
      set({
        error:
          error instanceof Error ? error.message : 'Failed to upload asset',
        isLoading: false,
      })
      throw error
    }
  },

  deleteAsset: async (configId: string, assetTypeOrId: string) => {
    set({ isLoading: true, error: null })

    try {
      await chatBuilderService.deleteAsset(configId, assetTypeOrId)

      storeLogger.debug('Asset deleted', {
        configId,
        assetTypeOrId,
      })

      // Update current configuration by removing asset
      // Handle both asset ID and asset type for backwards compatibility
      const { updateAssetsInCurrentConfiguration } = get()
      updateAssetsInCurrentConfiguration((existingAssets) =>
        existingAssets.filter(
          (a) => a.id !== assetTypeOrId && a.assetType !== assetTypeOrId
        )
      )

      set({ isLoading: false })
    } catch (error) {
      storeLogger.error('Failed to delete asset', error as Error)
      set({
        error:
          error instanceof Error ? error.message : 'Failed to delete asset',
        isLoading: false,
      })
      throw error
    }
  },

  // Internal method to update assets in current configuration
  updateAssetsInCurrentConfiguration: (
    updater: (assets: ChatConfigurationAsset[]) => ChatConfigurationAsset[]
  ) => {
    const { currentConfiguration } = get()
    if (!currentConfiguration) return

    const existingAssets = currentConfiguration.assets || []
    const updatedAssets = updater(existingAssets)

    set({
      currentConfiguration: {
        ...currentConfiguration,
        assets: updatedAssets,
      },
    })
  },
})
